// @flow

import { useCallback, useState } from "react";
import styled, { type StyledComponent } from "styled-components";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import useFetch from "use-http";
import MiniChart from "./MiniChart";
import { type DateFilter } from "app/components/analytics/shared/DateFilters";
import InteractiveChart from "app/components/analytics/shared/InteractiveChart";
import Dialog from "app/components/shared/Dialog";

type Props = {
  modalHeading: string,
  label: string,
  filter: DateFilter,
  branch: ?string,
  metric: string,
  endpoint: string,
};

const StyledCard: StyledComponent<{}, empty, HTMLButtonElement> = styled.button`
  font-family:
    SF Pro Text,
    -apple-system,
    BlinkMacSystemFont,
    Segoe UI,
    Roboto,
    Helvetica Neue,
    Helvetica,
    sans-serif;
  border: none;
  border-radius: 4px;
  padding: 20px;
  background-color: var(--base-0);
  text-align: left;
  flex: none;
  justify-content: space-between;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  top: 0;
  transition: top ease 0.3s;
  box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.09),
    0px 1px 0px rgba(0, 0, 0, 0.09),
    0px 2px 2px rgba(0, 0, 0, 0.04),
    0px 3px 3px rgba(0, 0, 0, 0.02),
    0px 4px 4px rgba(0, 0, 0, 0.01);

  @media (min-width: 769px) {
    display: flex;
    align-items: stretch;
    flex: 1 1 0;
    flex-direction: column;
    max-width: 300px;
  }

  &:hover {
    top: -2px;
    box-shadow:
      0px 0px 0px 1px rgba(0, 0, 0, 0.09),
      0px 1px 0px rgba(0, 0, 0, 0.09),
      0px 2px 2px rgba(0, 0, 0, 0.04),
      0px 10px 14px rgba(0, 0, 0, 0.02),
      0px 6px 7px -2px rgba(0, 0, 0, 0.08);
  }

  span {
    color: var(--charcoal-500);
  }

  > div {
    @media (max-width: 486px) {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }
`;

StyledCard.displayName = "Card";

const StyledLabel = styled.span`
  display: inline-block;
  margin-bottom: 5px;
  font-size: 14px;
`;

const ChartCard = (props: Props) => {
  const url = new URL("chart_metric", props.endpoint);
  url.searchParams.append(
    "from",
    moment(props.filter.from).format("YYYY-MM-DD"),
  );
  url.searchParams.append("metric", props.metric);
  if (props.branch != null) {
    url.searchParams.append("branch", props.branch);
  }

  const { loading, error, data } = useFetch(url.href, [
    props.filter,
    props.branch,
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleModalClose = useCallback(() => setIsModalOpen(false));
  const handleCardOnClick = useCallback(() => setIsModalOpen(true));

  return (
    <>
      <StyledCard onClick={handleCardOnClick} className="depth-hover">
        <StyledLabel>{props.label}</StyledLabel>
        <div style={{ minWidth: "100%", overflow: "hidden" }}>
          {error && (
            <div>
              There was an error loading {props.label.toLowerCase()} data
            </div>
          )}
          {!error && (
            <div className="h1 bold mb4">
              {data != null && !loading ? (
                data
              ) : (
                <Skeleton containerTestId="loading-skeleton" />
              )}
            </div>
          )}
          <div>
            <MiniChart
              filter={props.filter}
              branch={props.branch}
              metric={props.metric}
              endpoint={props.endpoint}
            />
          </div>
        </div>
      </StyledCard>
      <Dialog
        isOpen={isModalOpen}
        onRequestClose={handleModalClose}
        width="80vw"
      >
        <div className="p4">
          <InteractiveChart {...props} heading={props.modalHeading} />
        </div>
      </Dialog>
    </>
  );
};

export default ChartCard;
